import * as React from 'react'

import { InFieldLabel, inFieldShellClass, isFieldFilled } from './field'
import { cn } from './utils'

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: React.ReactNode
  description?: string
  startContent?: React.ReactNode
  endContent?: React.ReactNode
  appendEndContent?: React.ReactNode
  fullWidth?: boolean
  disableEscapeKey?: boolean
  onEscape?: () => void
  variant?: 'default' | 'ghost' | 'plain'
  startContentPaddingLeft?: number
}
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      label,
      description,
      startContent,
      endContent,
      appendEndContent,
      fullWidth,
      disableEscapeKey,
      onEscape,
      onKeyDown,
      onFocus,
      onBlur,
      onChange,
      variant = 'default',
      id,
      value,
      defaultValue,
      placeholder,
      'aria-labelledby': _ariaLabelledBy,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId()
    const inputId = id ?? generatedId
    const [focused, setFocused] = React.useState(false)
    const isControlled = value !== undefined
    const [uncontrolledValue, setUncontrolledValue] = React.useState(
      defaultValue ?? ''
    )
    const currentValue = isControlled ? value : uncontrolledValue
    const floated = Boolean(label) && (focused || isFieldFilled(currentValue))
    const invalid = props['aria-invalid'] === true || props['aria-invalid'] === 'true'

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disableEscapeKey && e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        e.nativeEvent.stopImmediatePropagation()
        if (onEscape) {
          onEscape()
        }
        return
      }
      onKeyDown?.(e)
    }

    // Bare input — no wrapper — so it can sit inline and match surrounding text.
    if (variant === 'plain') {
      return (
        <input
          id={inputId}
          type={type === 'number' ? 'number' : type}
          className={cn(
            'input-plain min-w-0 text-inherit placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
          ref={ref}
          value={value}
          defaultValue={defaultValue}
          onKeyDown={handleKeyDown}
          onChange={onChange}
          {...props}
        />
      )
    }

    const labeled = Boolean(label)

    return (
      <div className={cn('relative w-full', fullWidth && 'w-full')}>
        <div
          className={cn(
            labeled && inFieldShellClass({ variant }),
            labeled && props.disabled && 'opacity-50'
          )}
        >
          {label && (
            <InFieldLabel
              htmlFor={inputId}
              floated={floated}
              startContent={Boolean(startContent)}
              invalid={invalid}
            >
              {label}
            </InFieldLabel>
          )}

          <input
            id={inputId}
            type={type === 'number' ? 'number' : type}
            className={cn(
              'flex w-full text-xs ring-offset-background file:border-0 file:bg-transparent file:text-sm file:text-foreground placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50',
              labeled
                ? cn(
                    'h-14 bg-transparent px-4 pb-2 pt-5 outline-none',
                    startContent ? 'pl-10' : 'pl-4',
                    !floated &&
                      'caret-transparent placeholder:text-transparent'
                  )
                : cn(
                    'h-10 rounded-md',
                    variant === 'ghost' ? 'input-ghost' : 'input-style',
                    startContent ? 'pl-8' : 'pl-3'
                  ),
              className,
              (endContent || appendEndContent) && 'pr-10'
            )}
            ref={ref}
            value={value}
            defaultValue={defaultValue}
            onKeyDown={handleKeyDown}
            onFocus={(event) => {
              setFocused(true)
              onFocus?.(event)
            }}
            onBlur={(event) => {
              setFocused(false)
              onBlur?.(event)
            }}
            onChange={(event) => {
              if (!isControlled) setUncontrolledValue(event.target.value)
              onChange?.(event)
            }}
            {...props}
            placeholder={labeled && !floated ? undefined : placeholder}
            aria-labelledby={label ? `${inputId}-label` : undefined}
          />
          {startContent && (
            <div className="absolute inset-y-0 left-2.5 flex items-center">
              {startContent}
            </div>
          )}
          {endContent && (
            <div className="absolute inset-y-0 right-2 flex items-center">
              {endContent}
            </div>
          )}
          {appendEndContent && (
            <div className="absolute inset-y-0 right-2 flex items-center">
              {appendEndContent}
            </div>
          )}
        </div>

        {description && (
          <p className="ml-1 mt-1 text-xs text-muted-foreground">
            {description}
          </p>
        )}
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input }
